import { create } from "zustand";
import { track } from "@/pwa/analytics";

export type Platform = "android" | "ios" | "desktop" | "unsupported";
export type InstallStatus =
  | "INSTALL_AVAILABLE"
  | "INSTALL_PROMPT_SHOWN"
  | "INSTALL_ACCEPTED"
  | "INSTALL_DISMISSED"
  | "INSTALLED"
  | "IOS_INSTALL_INSTRUCTIONS"
  | "ALREADY_RUNNING_INSTALLED"
  | "INSTALL_UNSUPPORTED";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

let deferredPrompt: BeforeInstallPromptEvent | null = null;
let initialised = false;

const DISMISS_KEY = "buildease.pwa.dismissedAt";
const DISMISS_DAYS = 7;

function isStandaloneMode(): boolean {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    (navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

function detectPlatform(): Platform {
  const ua = navigator.userAgent || "";
  const iOS = /iphone|ipad|ipod/i.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  if (iOS) return "ios";
  if (/android/i.test(ua)) return "android";
  return "desktop";
}

function dismissedRecently(): boolean {
  const ts = Number(localStorage.getItem(DISMISS_KEY) || 0);
  return !!ts && Date.now() - ts < DISMISS_DAYS * 86400000;
}

interface InstallState {
  platform: Platform;
  isStandalone: boolean;
  isInstalled: boolean;
  canPromptNative: boolean;
  status: InstallStatus;
  iosSheetOpen: boolean;
  bannerDismissed: boolean;
  init: () => void;
  promptInstall: () => Promise<void>;
  openIos: () => void;
  closeIos: () => void;
  dismissBanner: () => void;
}

export const useInstallStore = create<InstallState>((set, get) => ({
  platform: "unsupported",
  isStandalone: false,
  isInstalled: false,
  canPromptNative: false,
  status: "INSTALL_UNSUPPORTED",
  iosSheetOpen: false,
  bannerDismissed: false,

  init: () => {
    if (initialised || typeof window === "undefined") return;
    initialised = true;

    const standalone = isStandaloneMode();
    const platform = detectPlatform();
    set({
      platform,
      isStandalone: standalone,
      isInstalled: standalone,
      bannerDismissed: dismissedRecently(),
      status: standalone
        ? "ALREADY_RUNNING_INSTALLED"
        : platform === "ios"
          ? "IOS_INSTALL_INSTRUCTIONS"
          : "INSTALL_UNSUPPORTED",
    });
    if (standalone) track("standalone_launch");

    window.addEventListener("beforeinstallprompt", (e) => {
      e.preventDefault();
      deferredPrompt = e as BeforeInstallPromptEvent;
      set({
        canPromptNative: true,
        status: "INSTALL_AVAILABLE",
        platform: /android/i.test(navigator.userAgent) ? "android" : "desktop",
      });
    });

    window.addEventListener("appinstalled", () => {
      deferredPrompt = null;
      set({ isInstalled: true, canPromptNative: false, iosSheetOpen: false, status: "INSTALLED" });
      track("install_completed");
    });
  },

  promptInstall: async () => {
    if (!deferredPrompt) return;
    set({ status: "INSTALL_PROMPT_SHOWN" });
    track("install_prompt_shown");
    await deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    if (choice.outcome === "accepted") {
      set({ status: "INSTALL_ACCEPTED" });
      track("install_accepted");
    } else {
      set({ status: "INSTALL_DISMISSED" });
      track("install_prompt_dismissed");
      get().dismissBanner();
    }
    deferredPrompt = null;
    set({ canPromptNative: false });
  },

  openIos: () => {
    set({ iosSheetOpen: true, status: "IOS_INSTALL_INSTRUCTIONS" });
    track("install_guide_viewed", { platform: "ios" });
  },
  closeIos: () => set({ iosSheetOpen: false }),

  dismissBanner: () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    set({ bannerDismissed: true });
    track("install_prompt_dismissed");
  },
}));
